import { databases, functions, ID, Query, Permission, Role, tablesDB } from '../lib/appwrite';
import * as cache from './cache';

const DB_ID = "694ac32800098777afbf";
const COLLECTIONS = {
    ITEMS: "items",
    TRANSACTIONS: "transactions",
    PRODUCTION_RECORDS: "production_records",
    SALES_TRANSACTIONS: "sales_transactions"
};

export const api = {
    // ITEMS
    getItems: async (warehouseId, skipCache = false) => {
        try {
            // Check cache first (unless skipCache for critical operations)
            if (!skipCache) {
                const cached = cache.get(cache.CacheKeys.items(warehouseId));
                if (cached) return cached;
            }

            const queries = [Query.limit(1000)];
            if (warehouseId) {
                queries.push(Query.equal('warehouse_id', warehouseId));
            }
            const response = await databases.listDocuments(DB_ID, COLLECTIONS.ITEMS, queries);

            // Cache the result
            cache.set(cache.CacheKeys.items(warehouseId), response.documents, cache.TTL.MEDIUM);

            return response.documents;
        } catch (error) {
            console.error("Error fetching items:", error);
            return [];
        }
    },

    addItem: async (item, warehouseId) => {
        try {
            if (!warehouseId) throw new Error("Warehouse ID is required");
            const payload = { ...item, warehouse_id: warehouseId };
            const permissions = [
                Permission.read(Role.team(warehouseId)),
                Permission.write(Role.team(warehouseId))
            ];
            const result = await databases.createDocument(DB_ID, COLLECTIONS.ITEMS, ID.unique(), payload, permissions);
            cache.invalidate(cache.CacheKeys.items(warehouseId));
            return result;
        } catch (error) {
            console.error("Error adding item:", error);
            throw error;
        }
    },

    deleteItem: async (id, warehouseId) => {
        try {
            await databases.deleteDocument(DB_ID, COLLECTIONS.ITEMS, id);
            if (warehouseId) cache.invalidate(cache.CacheKeys.items(warehouseId));
            else cache.invalidate('items_*');
        } catch (error) {
            console.error("Error deleting item:", error);
            throw error;
        }
    },

    updateItemStock: async (id, newStock) => {
        try {
            return await databases.updateDocument(DB_ID, COLLECTIONS.ITEMS, id, {
                current_stock: newStock
            });
        } catch (error) {
            console.error("Error updating item stock:", error);
            throw error;
        }
    },

    updateItem: async (id, data, warehouseId) => {
        try {
            const result = await databases.updateDocument(DB_ID, COLLECTIONS.ITEMS, id, data);
            if (warehouseId) cache.invalidate(cache.CacheKeys.items(warehouseId));
            else cache.invalidate('items_*');
            return result;
        } catch (error) {
            console.error("Error updating item:", error);
            throw error;
        }
    },

    // TRANSACTIONS
    getTransactions: async (warehouseId) => {
        try {
            const cached = cache.get(cache.CacheKeys.transactions(warehouseId));
            if (cached) return cached;

            const queries = [
                Query.orderDesc('$createdAt'),
                Query.limit(1000)
            ];
            if (warehouseId) {
                queries.push(Query.equal('warehouse_id', warehouseId));
            }
            const response = await databases.listDocuments(DB_ID, COLLECTIONS.TRANSACTIONS, queries);

            cache.set(cache.CacheKeys.transactions(warehouseId), response.documents, cache.TTL.MEDIUM);

            return response.documents;
        } catch (error) {
            console.error("Error fetching transactions:", error);
            return [];
        }
    },

    getTodayTransactions: async (warehouseId) => {
        try {
            const cached = cache.get(cache.CacheKeys.todayTransactions(warehouseId));
            if (cached) return cached;

            const d = new Date();
            // Start of today
            d.setHours(0, 0, 0, 0);
            const startStr = d.toISOString();

            // End of today
            d.setHours(23, 59, 59, 999);
            const endStr = d.toISOString();

            const queries = [
                Query.orderDesc('$createdAt'),
                Query.limit(1000), // Max for today likely won't exceed this, but safe limit
                Query.greaterThanEqual('$createdAt', startStr),
                Query.lessThanEqual('$createdAt', endStr)
            ];

            if (warehouseId) {
                queries.push(Query.equal('warehouse_id', warehouseId));
            }

            const response = await databases.listDocuments(DB_ID, COLLECTIONS.TRANSACTIONS, queries);

            cache.set(cache.CacheKeys.todayTransactions(warehouseId), response.documents, cache.TTL.SHORT); // Shorter TTL for today as it changes often

            return response.documents;
        } catch (error) {
            console.error("Error fetching today's transactions:", error);
            return [];
        }
    },

    getLastTransaction: async (warehouseId) => {
        try {
            const queries = [
                Query.orderDesc('$createdAt'),
                Query.limit(1)
            ];
            if (warehouseId) {
                queries.push(Query.equal('warehouse_id', warehouseId));
            }
            const response = await databases.listDocuments(DB_ID, COLLECTIONS.TRANSACTIONS, queries);
            return response.documents[0] || null;
        } catch (error) {
            console.error("Error fetching last transaction:", error);
            return null;
        }
    },

    // SEARCH TRANSACTIONS (Two-Query Strategy for group integrity)
    // Step 1: Search with filters → get matching rows (paginated)
    // Step 2: Fetch ALL rows for matched transaction_codes → guarantees complete groups
    searchTransactions: async (warehouseId, {
        searchText = '',
        searchField = 'name',
        type = '',
        dateFrom = '',
        dateTo = '',
        minQty = '',
        offset = 0,
        pageSize = 150
    } = {}) => {
        try {
            // === STEP 1: Search query with filters ===
            const queries = [
                Query.orderDesc('$createdAt'),
                Query.limit(pageSize),
                Query.offset(offset),
            ];

            if (warehouseId) {
                queries.push(Query.equal('warehouse_id', warehouseId));
            }

            // Search text filter
            if (searchText && searchText.trim()) {
                const text = searchText.trim();
                switch (searchField) {
                    case 'code':
                        queries.push(Query.startsWith('transaction_code', text));
                        break;
                    case 'item':
                        queries.push(Query.contains('item_id', text));
                        break;
                    case 'user':
                        queries.push(Query.contains('user_name', text));
                        break;
                    case 'name':
                    default:
                        queries.push(Query.contains('notes', text));
                        break;
                }
            }

            // Type filter
            if (type) {
                queries.push(Query.equal('type', type));
            }

            // Date range filter
            if (dateFrom) {
                queries.push(Query.greaterThanEqual('date', dateFrom));
            }
            if (dateTo) {
                queries.push(Query.lessThanEqual('date', dateTo));
            }

            // Min quantity filter
            if (minQty && Number(minQty) > 0) {
                queries.push(Query.greaterThanEqual('quantity', Number(minQty)));
            }

            const searchResult = await databases.listDocuments(DB_ID, COLLECTIONS.TRANSACTIONS, queries);
            const hasMore = searchResult.documents.length === pageSize;

            if (searchResult.documents.length === 0) {
                return { documents: [], hasMore: false, fetchedRowCount: 0 };
            }

            // Extract unique transaction codes
            const codes = [...new Set(
                searchResult.documents.map(d => d.transaction_code).filter(Boolean)
            )];

            if (codes.length === 0) {
                return { documents: searchResult.documents, hasMore, fetchedRowCount: searchResult.documents.length };
            }

            // === STEP 2: Completion query — fetch ALL rows for matched codes ===
            const completionQueries = [
                Query.equal('transaction_code', codes),
                Query.orderDesc('$createdAt'),
                Query.limit(5000),
            ];

            if (warehouseId) {
                completionQueries.push(Query.equal('warehouse_id', warehouseId));
            }

            const completeResult = await databases.listDocuments(DB_ID, COLLECTIONS.TRANSACTIONS, completionQueries);

            return {
                documents: completeResult.documents,
                hasMore,
                fetchedRowCount: searchResult.documents.length
            };
        } catch (error) {
            console.error("Error searching transactions:", error);
            return { documents: [], hasMore: false, fetchedRowCount: 0 };
        }
    },

    // generateNextCode: async (prefix, warehouseId) => {
    //     // prefix: 'W' | 'M' | 'P'
    //     try {
    //         const lastTx = await api.getLastTransaction(warehouseId);
    //         let nextNum = 1;
    //         if (lastTx && lastTx.transaction_code) {
    //             const parts = lastTx.transaction_code.split('-');
    //             if (parts.length === 2) {
    //                 const num = parseInt(parts[1], 10);
    //                 if (!isNaN(num)) {
    //                     nextNum = num + 1;
    //                 }
    //             }
    //         }
    //         return `${prefix}-${nextNum}`;
    //     } catch (error) {
    //         console.error("Error generating next code:", error);
    //         // Fallback
    //         return `${prefix}-${Date.now()}`;
    //     }
    // },


    addTransaction: async (data, itemToUpdate, warehouseId) => {
        // data: { item_id, the_item_id, type, quantity, date, notes, transaction_code, status }
        // itemToUpdate: { id, newStock } (Optional, if provided we update stock locally)
        try {
            if (!warehouseId) throw new Error("Warehouse ID is required");

            const payload = {
                ...data,
                warehouse_id: warehouseId,
                received: Number(data.received) || 0,
                remaining: Number(data.remaining) || 0
            };

            const permissions = [
                Permission.read(Role.team(warehouseId)),
                Permission.write(Role.team(warehouseId))
            ];

            const transaction = await databases.createDocument(DB_ID, COLLECTIONS.TRANSACTIONS, ID.unique(), payload, permissions);

            if (itemToUpdate) {
                await api.updateItemStock(itemToUpdate.id, itemToUpdate.newStock);
            }
            return transaction;
        } catch (error) {
            console.error("Error adding transaction:", error);
            throw error;
        }
    },

    deleteTransaction: async (id) => {
        try {
            await databases.deleteDocument(DB_ID, COLLECTIONS.TRANSACTIONS, id);
        } catch (error) {
            console.error("Error deleting transaction:", error);
            throw error;
        }
    },

    // PRODUCTION RECORDS
    getProductionRecords: async (warehouseId) => {
        try {
            const queries = [
                Query.orderDesc('date'),
                Query.limit(1000)
            ];
            if (warehouseId) {
                queries.push(Query.equal('warehouse_id', warehouseId));
            }
            const response = await databases.listDocuments(DB_ID, COLLECTIONS.PRODUCTION_RECORDS, queries);
            return response.documents;
        } catch (error) {
            console.error("Error fetching production records:", error);
            return [];
        }
    },

    getWarehouseItem: async (name, warehouseId) => {
        try {
            const response = await databases.listDocuments(DB_ID, COLLECTIONS.ITEMS, [
                Query.equal('name', name),
                Query.equal('warehouse_id', warehouseId),
                Query.limit(1)
            ]);
            return response.documents[0] || null;
        } catch (error) {
            console.error(`Error fetching item ${name}:`, error);
            return null;
        }
    },

    addProductionRecord: async (record, consumedAmount, warehouseId) => {
        // record: { date, sack_size, quantity, total_weight, consumed_50kg, prod_notes }

        try {
            if (!warehouseId) throw new Error("Warehouse ID is required");

            // 1. Validation: Find 50kg Stock
            const sugar50kgDoc = await api.getWarehouseItem("سكر 50 كيلو", warehouseId);
            if (!sugar50kgDoc) throw new Error("لم يتم العثور على صنف 'سكر 50 كيلو' في هذا المستودع.");

            const currentStock50 = sugar50kgDoc.current_stock || 0;

            if (currentStock50 < consumedAmount) {
                throw new Error(`لا يوجد رصيد كافي من "سكر 50 كيلو". الرصيد الحالي: ${currentStock50}، المطلوب: ${consumedAmount}`);
            }

            // 2. Prepare Stock Updates
            const newStock50 = currentStock50 - consumedAmount;

            // Determine Produced Item Name (Assuming standard naming convention)
            // const SACK_10KG_NAME = "سكر 10 كيلو";
            // const SACK_5KG_NAME = "سكر 5 كيلو";
            const producedItemName = (record.sack_size === 10) ? "سكر 10 كيلو" : "سكر 5 كيلو";

            // Fetch Produced Item Stock
            const producedItemDoc = await api.getWarehouseItem(producedItemName, warehouseId);
            if (!producedItemDoc) throw new Error(`لم يتم العثور على صنف '${producedItemName}' في هذا المستودع.`);

            const currentStockProduced = producedItemDoc.current_stock || 0;
            const newStockProduced = currentStockProduced + record.quantity; // Quantity is number of sacks produced

            // 3. Execution (Sequential)

            // A. Create Production Record
            const prodPayload = { ...record, warehouse_id: warehouseId };
            const permissions = [
                Permission.read(Role.team(warehouseId)),
                Permission.write(Role.team(warehouseId))
            ];

            const prodDoc = await databases.createDocument(DB_ID, COLLECTIONS.PRODUCTION_RECORDS, ID.unique(), prodPayload, permissions);

            // B. Update 50kg Stock (Deduct)
            await api.updateItemStock(sugar50kgDoc.$id, newStock50);

            // C. Update Produced Item Stock (Add)
            await api.updateItemStock(producedItemDoc.$id, newStockProduced);

            // D. Create Transaction
            // Generate Code P-XXX
            const newCode = await api.generateNextCode('P', warehouseId);

            // Create Transaction Document
            await databases.createDocument(DB_ID, COLLECTIONS.TRANSACTIONS, ID.unique(), {
                item_id: producedItemName, // "سكر 5 كيلو"
                the_item_id: producedItemDoc.$id,
                warehouse_id: warehouseId,
                type: 'production',
                quantity: record.quantity, // Produced Qty
                date: record.date,
                notes: record.prod_notes || `إنتاج ${record.quantity} جوال (${record.sack_size} كيلو)`,
                transaction_code: newCode,
                status: 'completed',
                consumed_50kg: consumedAmount, // New field
                received: record.quantity,
                remaining: 0,
                user_name: record.user_name // Save user name
            }, permissions);

            return prodDoc;

        } catch (error) {
            console.error("Error adding production record:", error);
            throw error;
        }
    },

    deleteProductionRecord: async (id) => {
        try {
            await databases.deleteDocument(DB_ID, COLLECTIONS.PRODUCTION_RECORDS, id);
        } catch (error) {
            console.error("Error deleting production record:", error);
            throw error;
        }
    },

    // EDIT TRANSACTION GROUP
    updateTransactionGroup: async (originalGroup, newGroupData, warehouseId) => {
        // originalGroup: { id (code), items: [{id, name, qty, consumed_50kg (if prod), the_item_id}] }
        // newGroupData: { code, type, date, notes, status, items: [{name, qty, consumed_50kg}] }

        const rollbackOperations = [];

        try {
            if (!warehouseId) throw new Error("Warehouse ID is required");

            const permissions = [
                Permission.read(Role.team(warehouseId)),
                Permission.write(Role.team(warehouseId))
            ];

            // =================================================================================
            // SCENARIO A: PRODUCTION EDIT
            // =================================================================================
            if (newGroupData.type === 'production') {
                const sugar50kgDoc = await api.getWarehouseItem("سكر 50 كيلو", warehouseId);
                if (!sugar50kgDoc) throw new Error("سكر 50 كيلو غير موجود");

                // 1. Prepare Data & Validations
                const oldItem = originalGroup.items[0];
                const oldProducedQty = oldItem.qty || 0;
                const oldConsumed50 = oldItem.consumed_50kg || 0;
                const producedItemId = oldItem.the_item_id;

                const producedItemDoc = await databases.getDocument(DB_ID, COLLECTIONS.ITEMS, producedItemId);

                const initialStock50 = sugar50kgDoc.current_stock || 0;
                const initialStockProduced = producedItemDoc.current_stock || 0;

                // Logical Revert of Old Transaction influence
                const stock50AfterRevert = initialStock50 + oldConsumed50;
                const stockProducedAfterRevert = initialStockProduced - oldProducedQty;

                // Calculate New Transaction influence
                const newItem = newGroupData.items[0];
                const newProducedQty = Number(newItem.qty) || 0;

                let newConsumed50 = 0;
                if (newItem.consumed_50kg && !isNaN(newItem.consumed_50kg) && Number(newItem.consumed_50kg) > 0) {
                    newConsumed50 = Number(newItem.consumed_50kg);
                } else {
                    let sackSize = 5;
                    if (newItem.name.includes('10')) sackSize = 10;
                    const totalWeight = newProducedQty * sackSize;
                    newConsumed50 = Math.ceil(totalWeight / 50);
                }

                // Validation
                if (stock50AfterRevert < newConsumed50) {
                    throw new Error(`لا يوجد رصيد كافي من "سكر 50 كيلو" للتعديل. الرصيد (بعد الاسترجاع): ${stock50AfterRevert}، المطلوب: ${newConsumed50}`);
                }

                // Final Stocks
                const finalStock50 = stock50AfterRevert - newConsumed50;
                const finalStockProduced = stockProducedAfterRevert + newProducedQty;

                // 2. Execute Stock Updates (With Rollback Tracking)
                await api.updateItemStock(sugar50kgDoc.$id, finalStock50);
                rollbackOperations.push({ id: sugar50kgDoc.$id, resetTo: initialStock50 });

                await api.updateItemStock(producedItemId, finalStockProduced);
                rollbackOperations.push({ id: producedItemId, resetTo: initialStockProduced });

                // 3. Execute Document Update
                await databases.updateDocument(DB_ID, COLLECTIONS.TRANSACTIONS, oldItem.id, {
                    quantity: newProducedQty,
                    consumed_50kg: newConsumed50,
                    notes: newGroupData.notes,
                    date: newGroupData.date,
                });

                return true;
            }

            // =================================================================================
            // SCENARIO B: STANDARD EDIT (IN / OUT)
            // =================================================================================
            const isOut = newGroupData.type === 'out';
            const typeMultiplier = isOut ? -1 : 1;

            // 1. Calculate Net Stock Changes
            const stockChanges = new Map();

            // Reverse Old
            originalGroup.items.forEach(item => {
                const reversal = -1 * (item.qty * typeMultiplier);
                const current = stockChanges.get(item.name) || 0;
                stockChanges.set(item.name, current + reversal);
            });

            // Apply New
            newGroupData.items.forEach(item => {
                const application = item.qty * typeMultiplier;
                const current = stockChanges.get(item.name) || 0;
                stockChanges.set(item.name, current + application);
            });

            // 2. Fetch & Validate Items
            const itemNames = Array.from(stockChanges.keys());
            const dbItemsMap = new Map();

            for (const name of itemNames) {
                const resp = await databases.listDocuments(DB_ID, COLLECTIONS.ITEMS, [
                    Query.equal('name', name),
                    Query.equal('warehouse_id', warehouseId)
                ]);
                if (resp.documents.length > 0) {
                    const doc = resp.documents[0];
                    dbItemsMap.set(name, doc);
                } else {
                    throw new Error(`الصنف غير موجود: ${name}`);
                }
            }

            // Validate negative stock (for Out transaction logic mainly)
            for (const [name, delta] of stockChanges.entries()) {
                if (delta === 0) continue;
                const dbItem = dbItemsMap.get(name);
                const currentStock = dbItem.current_stock || 0;
                if (currentStock + delta < 0) {
                    throw new Error(`لا يوجد رصيد كافي للصنف ${name}. الرصيد الحالي: ${currentStock}, التغيير المطلوب يؤدي إلى سالب.`);
                }
            }

            // 3. Execute Stock Updates (With Rollback Tracking)
            for (const [name, delta] of stockChanges.entries()) {
                if (delta === 0) continue;
                const dbItem = dbItemsMap.get(name);
                const oldStock = dbItem.current_stock || 0;
                const newStock = oldStock + delta;

                await api.updateItemStock(dbItem.$id, newStock);
                rollbackOperations.push({ id: dbItem.$id, resetTo: oldStock });
            }

            // 4. Execute Document Updates
            const oldDocs = new Map(originalGroup.items.map(i => [i.name, i.id])); // Name -> DocID
            const newItems = [...newGroupData.items];
            const processedIds = new Set();

            for (const newItem of newItems) {
                const dbItem = dbItemsMap.get(newItem.name);

                if (oldDocs.has(newItem.name)) {
                    // Update existing
                    const docId = oldDocs.get(newItem.name);
                    await databases.updateDocument(DB_ID, COLLECTIONS.TRANSACTIONS, docId, {
                        quantity: Number(newItem.qty),
                        notes: newGroupData.notes,
                        date: newGroupData.date,
                        type: newGroupData.type,
                        status: newGroupData.status,
                        received: Number(newItem.received) || 0,
                        remaining: Number(newItem.remaining) || 0
                    });
                    processedIds.add(docId);
                    oldDocs.delete(newItem.name);
                } else {
                    // Create New
                    await databases.createDocument(DB_ID, COLLECTIONS.TRANSACTIONS, ID.unique(), {
                        transaction_code: newGroupData.code,
                        item_id: newItem.name,
                        the_item_id: dbItem.$id,
                        warehouse_id: warehouseId,
                        type: newGroupData.type,
                        quantity: Number(newItem.qty),
                        date: newGroupData.date,
                        notes: newGroupData.notes,
                        status: newGroupData.status,
                        received: Number(newItem.received) || 0,
                        remaining: Number(newItem.remaining) || 0
                    }, permissions);
                }
            }

            // Delete leftovers
            for (const docId of oldDocs.values()) {
                await databases.deleteDocument(DB_ID, COLLECTIONS.TRANSACTIONS, docId);
            }

            return true;

        } catch (error) {
            console.error("Update Failed. Rolling back stock changes...", error);

            // ROLLBACK EXECUTION
            for (let i = rollbackOperations.length - 1; i >= 0; i--) {
                const op = rollbackOperations[i];
                try {
                    await api.updateItemStock(op.id, op.resetTo);
                    console.log(`Rolled back item ${op.id} to ${op.resetTo}`);
                } catch (rbErr) {
                    console.error(`Failed to rollback item ${op.id}`, rbErr);
                }
            }
            throw error;
        }
    },

    // FUNCTIONS
    executeCreateTransaction: async (data, warehouseId) => {
        // data: { items: [], type, date, notes, incoming_cert, receipt_status, receiver }
        // warehouseId: string
        try {
            // Function ID needs to be provided or hardcoded once known. 
            // For now we will expect it to be passed or configured.
            // Let's assume the user will set it in a constant or env var, or provides it.
            // Using a placeholder for now.
            const FUNCTION_ID = "create_transaction"; // User needs to update this or map it

            const payload = JSON.stringify({
                ...data,
                warehouse_id: warehouseId,
                user_name: data.user_name || 'System'
            });

            const execution = await functions.createExecution(
                FUNCTION_ID,
                payload,
                false // Async = false (we want to wait for result to ensure atomicity/error)
            );

            if (execution.status === 'completed') {
                const response = JSON.parse(execution.responseBody);
                if (response.success) {
                    // Invalidate caches after successful transaction
                    cache.invalidate(cache.CacheKeys.items(warehouseId));
                    cache.invalidate(cache.CacheKeys.transactions(warehouseId));
                    cache.invalidate(cache.CacheKeys.todayTransactions(warehouseId));
                    return response;
                } else {
                    throw new Error(response.message || 'Function execution returned failure.');
                }
            } else {
                // Attempt to parse body even if failed, as we return 500 with JSON
                try {
                    const response = JSON.parse(execution.responseBody);
                    if (response && response.message) {
                        throw new Error(response.message);
                    }
                } catch (e) {
                    // ignore parsing error
                }
                // Fallback to stderr or generic status
                let errorMsg = execution.responseBody || execution.stderr;
                if (!errorMsg || errorMsg.includes("Execution timed out.")) {
                    errorMsg = "حدث خطاء في الشبكة ، تفقد المعاملات السابقة للتأكد من نجاح المعاملة او فشلها قبل المحاولة مرة أخرى";
                } else if (errorMsg.includes(`Function execution failed with status: ${execution.status}`)) {
                    errorMsg = "حدث خطاء في الشبكة ، تفقد المعاملات السابقة للتأكد من نجاح المعاملة او فشلها قبل المحاولة مرة أخرى";
                }
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("Error executing create transaction function:", error);
            throw error;
        }
    },

    executeEditTransaction: async (originalGroup, newGroupData, warehouseId) => {
        try {
            const FUNCTION_ID = "edit_transaction";

            const payload = JSON.stringify({
                originalGroup,
                newGroupData,
                warehouse_id: warehouseId
            });

            const execution = await functions.createExecution(
                FUNCTION_ID,
                payload,
                false // Async = false
            );

            if (execution.status === 'completed') {
                const response = JSON.parse(execution.responseBody);
                if (response.success) {
                    // Invalidate caches after successful edit
                    cache.invalidate(cache.CacheKeys.items(warehouseId));
                    cache.invalidate(cache.CacheKeys.transactions(warehouseId));
                    cache.invalidate(cache.CacheKeys.todayTransactions(warehouseId));
                    return response;
                } else {
                    throw new Error(response.message || 'Function execution returned failure.');
                }
            } else {
                try {
                    const response = JSON.parse(execution.responseBody);
                    if (response && response.message) {
                        throw new Error(response.message);
                    }
                } catch (e) { }
                let errorMsg = execution.responseBody || execution.stderr;
                if (!errorMsg || errorMsg.includes("Execution timed out.")) {
                    errorMsg = "حدث خطاء في الشبكة ، تفقد المعاملات السابقة للتأكد من نجاح المعاملة او فشلها قبل المحاولة مرة أخرى";
                }
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("Error executing edit transaction function:", error);
            throw error;
        }
    },

    executeCreateProduction: async (record, consumedAmount, warehouseId) => {
        try {
            const FUNCTION_ID = "create_production";

            const payload = JSON.stringify({
                record,
                consumedAmount,
                warehouse_id: warehouseId
            });

            const execution = await functions.createExecution(
                FUNCTION_ID,
                payload,
                false
            );

            if (execution.status === 'completed') {
                const response = JSON.parse(execution.responseBody);
                if (response.success) {
                    // Invalidate caches after successful production
                    cache.invalidate(cache.CacheKeys.items(warehouseId));
                    cache.invalidate(cache.CacheKeys.transactions(warehouseId));
                    cache.invalidate(cache.CacheKeys.todayTransactions(warehouseId));
                    return response;
                } else {
                    throw new Error(response.message || 'Production function execution returned failure.');
                }
            } else {
                try {
                    const response = JSON.parse(execution.responseBody);
                    if (response && response.message) {
                        throw new Error(response.message);
                    }
                } catch (e) { }
                let errorMsg = execution.responseBody || execution.stderr;
                if (!errorMsg || errorMsg.includes("Execution timed out.")) {
                    errorMsg = "حدث خطاء في الشبكة ، تفقد المعاملات السابقة للتأكد من نجاح المعاملة او فشلها قبل المحاولة مرة أخرى";
                }
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("Error executing create production function:", error);
            throw error;
        }
    },

    // SALES TRANSACTIONS (Using Appwrite Transactions API)
    createSaleTransaction: async (data, warehouseId) => {
        // data: { items: [], type: 'بيع'|'وارد', incoming_cert, receiver, user_name }
        try {
            // 1. Generate Transaction Code
            const prefix = data.type === 'بيع' ? 'S' : 'SW';
            const randomNums = Math.floor(1000000 + Math.random() * 9000000); // 7 digits
            const transactionCode = `${prefix}-${randomNums}`;

            // 2. Prepare stock update operations for tablesDB
            const stockOperations = [];

            // A. Create Transaction (Parent) for atomic stock updates
            const tx = await tablesDB.createTransaction();

            // Prepare Stock Operations
            for (const item of data.items) {
                const itemRefId = item.id || item.$id;
                const qty = parseInt(item.qty, 10) || 0;

                if (data.type === 'بيع') {
                    stockOperations.push({
                        action: 'decrement',
                        databaseId: DB_ID,
                        tableId: COLLECTIONS.ITEMS,
                        rowId: itemRefId,
                        data: {
                            value: qty,
                            column: 'current_stock',
                            min: 0
                        }
                    });
                } else {
                    stockOperations.push({
                        action: 'increment',
                        databaseId: DB_ID,
                        tableId: COLLECTIONS.ITEMS,
                        rowId: itemRefId,
                        data: {
                            value: qty,
                            column: 'current_stock'
                        }
                    });
                }
            }

            // 3. Stage and commit stock operations atomically
            await tablesDB.createOperations({
                transactionId: tx.$id,
                operations: stockOperations
            });

            await tablesDB.updateTransaction({
                transactionId: tx.$id,
                commit: true
            });

            // 4. Create sales transaction records using databases.createDocument (with permissions)
            const permissions = [
                Permission.read(Role.team(warehouseId)),
                // Permission.write(Role.team(warehouseId))
            ];

            for (const item of data.items) {
                const itemRefId = item.id || item.$id;
                const qty = parseInt(item.qty, 10) || 0;
                const cost = parseInt(item.price || 0, 10);
                const unitPrice = parseInt(item.unitPrice || (qty > 0 ? cost / qty : 0), 10);

                await databases.createDocument(
                    DB_ID,
                    COLLECTIONS.SALES_TRANSACTIONS,
                    ID.unique(),
                    {
                        item_id: itemRefId,
                        item_name: item.name,
                        quantity: qty,
                        warehouse_id: warehouseId,
                        user_name: data.user_name || 'System',
                        sale_notes: data.type === 'بيع' ? (data.receiver || '') : (data.incoming_cert || 'وارد'),
                        sale_transaction_code: transactionCode,
                        total_cost: cost,
                        unit_price: unitPrice,
                        unit_cost: parseInt(item.unitCost || 0, 10),
                        total_unit_cost: parseInt(item.unitCost || 0, 10) * qty
                    },
                    permissions
                );
            }

            // Invalidate sales transaction caches
            cache.invalidate(`salesTx_${warehouseId}*`);
            cache.invalidate(`items_${warehouseId}*`);

            return { success: true, code: transactionCode };

        } catch (error) {
            console.error("Transaction Failed:", error);
            // Attempt Rollback if tx exists and error occurred during commit phase logic
            // But usually API handles it. If validation failed in `createOperations`, we might need to rollback.
            // We assume error thrown means we should rollback if we have a tx.$id... 
            // but tx is local scope.
            // Ideally we'd wrap this. For now, rely on Appwrite to timeout or we catch and rethrow.
            throw error;
        }
    },

    getSalesTransactions: async (warehouseId) => {
        try {
            const cacheKey = cache.CacheKeys.salesTransactions(warehouseId);
            const cached = cache.get(cacheKey);
            if (cached) return cached;

            const response = await databases.listDocuments(
                DB_ID,
                COLLECTIONS.SALES_TRANSACTIONS,
                [
                    Query.equal('warehouse_id', warehouseId),
                    Query.orderDesc('$createdAt'),
                    Query.limit(100)
                ]
            );

            cache.set(cacheKey, response.documents, cache.TTL.MEDIUM);
            return response.documents;
        } catch (error) {
            console.error("Error fetching sales transactions:", error);
            throw error;
        }
    },

    getSalesTransactionsByDate: async (warehouseId, dateString) => {
        try {
            const cacheKey = cache.CacheKeys.salesTransactionsByDate(warehouseId, dateString);
            const cached = cache.get(cacheKey);
            if (cached) return cached;

            // Create date range for the selected day
            const startDate = new Date(dateString);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(dateString);
            endDate.setHours(23, 59, 59, 999);

            const response = await databases.listDocuments(
                DB_ID,
                COLLECTIONS.SALES_TRANSACTIONS,
                [
                    Query.equal('warehouse_id', warehouseId),
                    Query.greaterThanEqual('$createdAt', startDate.toISOString()),
                    Query.lessThanEqual('$createdAt', endDate.toISOString()),
                    Query.limit(1000)
                ]
            );

            cache.set(cacheKey, response.documents, cache.TTL.MEDIUM);
            return response.documents;
        } catch (error) {
            console.error("Error fetching sales transactions by date:", error);
            throw error;
        }
    },

    getSalesTransactionsByMonth: async (warehouseId, year, month) => {
        try {
            const cacheKey = cache.CacheKeys.salesTransactionsByMonth(warehouseId, year, month);
            const cached = cache.get(cacheKey);
            if (cached) return cached;

            // Create date range for the selected month
            const startDate = new Date(year, month - 1, 1, 0, 0, 0, 0);
            const endDate = new Date(year, month, 0, 23, 59, 59, 999); // Last day of month

            const response = await databases.listDocuments(
                DB_ID,
                COLLECTIONS.SALES_TRANSACTIONS,
                [
                    Query.equal('warehouse_id', warehouseId),
                    Query.greaterThanEqual('$createdAt', startDate.toISOString()),
                    Query.lessThanEqual('$createdAt', endDate.toISOString()),
                    Query.limit(5000)
                ]
            );

            cache.set(cacheKey, response.documents, cache.TTL.MEDIUM);
            return response.documents;
        } catch (error) {
            console.error("Error fetching sales transactions by month:", error);
            throw error;
        }
    },

    getTransactionsByCode: async (code) => {
        try {
            const response = await databases.listDocuments(
                DB_ID,
                COLLECTIONS.SALES_TRANSACTIONS,
                [
                    Query.equal('sale_transaction_code', code)
                ]
            );
            return response.documents;
        } catch (error) {
            console.error("Error fetching transactions by code:", error);
            throw error;
        }
    },

    // Bulk increase item prices using Appwrite transactions
    bulkIncreaseItemPrices: async (items, percentage, excludedIds = []) => {
        try {
            // Filter out excluded items
            const itemsToUpdate = items.filter(item => !excludedIds.includes(item.id));

            if (itemsToUpdate.length === 0) {
                return { success: true, updatedCount: 0 };
            }

            // Create transaction
            const tx = await tablesDB.createTransaction();

            // Prepare update operations
            const operations = itemsToUpdate.map(item => {
                const oldCost = parseFloat(item.cost) || 0;
                const oldPrice = parseFloat(item.price) || 0;

                // Calculate new prices (rounded to integers)
                const newCost = Math.round(oldCost * (1 + percentage / 100));
                const newSellPrice = Math.round(oldPrice * (1 + percentage / 100));

                return {
                    action: 'update',
                    databaseId: DB_ID,
                    tableId: COLLECTIONS.ITEMS,
                    rowId: item.id,
                    data: {
                        cost: newCost,
                        sell_price: newSellPrice
                    }
                };
            });

            // Stage operations
            await tablesDB.createOperations({
                transactionId: tx.$id,
                operations: operations
            });

            // Commit transaction
            await tablesDB.updateTransaction({
                transactionId: tx.$id,
                commit: true
            });

            // Invalidate items cache so next fetch gets updated prices
            cache.invalidate('items_*');

            return { success: true, updatedCount: itemsToUpdate.length };
        } catch (error) {
            console.error("Error in bulk price increase:", error);
            throw error;
        }
    }
};
